import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const ThemeContext = createContext();

// Theme is NOT persisted to localStorage — it is always sourced from the
// user's settings record in the DB (see Header.jsx, which calls setTheme once
// getMySettings() resolves). Before that call lands, fall back to OS
// preference so there's no flash of the wrong theme.
const getInitialTheme = () => {
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
};

// Toggle the `.dark` class on <html> — Tailwind's darkMode: 'class' hook.
const applyTheme = (theme) => {
  document.documentElement.classList.toggle("dark", theme === "dark");
};

export default function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Stable identities — setTheme is a dependency of SettingsContext's
  // fetchSettings, which is in turn a dependency of its bootstrap effect. If
  // this were recreated every render, toggling the theme would cascade into
  // re-running that effect and re-fetching settings from the API on every
  // toggle (a visible full-app "refresh").
  const setTheme = useCallback((next) => {
    setThemeState(next === "dark" ? "dark" : "light");
  }, []);
  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({ theme, isDark: theme === "dark", setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// Convenience hook: const { theme, toggleTheme } = useTheme();
export const useTheme = () => useContext(ThemeContext);
